import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useRef, useCallback, useEffect } from 'react';

const TextDiff = ({ originalText, compareText, showCaret = true, typedTextStyle, caretStyle, }) => {
    const chars = Array.from(originalText);
    return (jsxs(Fragment, { children: [chars.map((char, index) => {
                let style = {};
                if (compareText.length >= index + 1) {
                    style = typedTextStyle || style;
                }
                else if (showCaret && compareText.length === index) {
                    style = caretStyle || style;
                }
                return char === '\n' ? (jsx("br", {}, void 0)) : (jsx("span", Object.assign({ style: style }, { children: char }), index));
            }),
            showCaret && compareText.length === originalText.length && (jsx("span", Object.assign({ style: caretStyle }, { children: "\u00A0" }), void 0))] }, void 0));
};

function leftPadZeroes(string, length) {
    if (string.length < length) {
        string = '0'.repeat(length - string.length) + string;
    }
    return string;
}
/**
 * Formats the number of seconds in a human-readable time string.
 *
 * @param totalSeconds - The number of seconds to format
 * @returns String in the format "m:ss" ("m" = minutes, "ss" = seconds left-padded with zeroes)
 */
function formatSeconds(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${leftPadZeroes(seconds.toString(), 2)}`;
}
/**
 * Returns the number of milliseconds until the next whole second after
 * `currentTimestamp`, relative to `startTimestamp`. Example:
 *
 *   nextWholeSecond(1200, 1500) // 700 (ms until 2200, 1 second after 1200)
 *   nextWholeSecond(2500, 4400) // 100
 *
 * @param startTimestamp - The timestamp to use as the base relative time
 * @param currentTimestamp - The timestamp we are comparing to (defaults to now)
 * @returns The number of milliseconds until the next whole second
 */
function msUntilNextSecond(startTimestamp, currentTimestamp) {
    currentTimestamp = currentTimestamp || Date.now();
    const ellapsed = currentTimestamp - startTimestamp;
    return 1000 - (ellapsed % 1000);
}

var styles = {"container":"TypingTest-module_container__1h21K","text":"TypingTest-module_text__1porL","input":"TypingTest-module_input__2o5mW"};

const TypingTest = ({ text, imageSrc, imageAltText, timeout, disabled, autoFocus = false, backgroundTextColor, overlayTextColor, useNativeCaret = false, backgroundTextStyle = {}, textareaStyle = {}, onTick, onKeyPress, onChange, allowFreeText = false, }) => {
    // `startTimestamp` will never change so we don't need the setter.
    const [startTimestamp] = useState(Date.now());
    const [secondsElapsed, setSecondsElapsed] = useState(0);
    const [typedText, setTypedText] = useState('');
    const [focused, setFocused] = useState(false);
    const inputRef = useRef(null);
    const timedOut = secondsElapsed >= timeout;
    const hasImage = !!imageSrc;
    const textBoxDisabled = disabled || timedOut;
    const updateElapsed = useCallback(() => {
        const msElapsed = Date.now() - startTimestamp;
        const secondsElapsed = Math.floor(msElapsed / 1000);
        // Make sure we don't set this beyond the timeout.
        const seconds = Math.min(secondsElapsed, timeout);
        setSecondsElapsed(seconds);
    }, [startTimestamp, timeout]);
    const tick = useCallback(() => {
        updateElapsed();
        // We need to add some time to adjust for the lack of precision in
        // these timers; otherwise, it's possible we "tick" early and don't
        // add a second to the elapsed time.
        // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/now#reduced_time_precision
        const timeoutMs = msUntilNextSecond(startTimestamp, Date.now()) + 2;
        const timer = window.setTimeout(() => {
            updateElapsed();
        }, timeoutMs);
        return () => {
            window.clearTimeout(timer);
        };
    }, [startTimestamp, updateElapsed]);
    useEffect(() => {
        if (disabled || timedOut) {
            return;
        }
        updateElapsed();
        return tick();
    }, [tick, disabled, secondsElapsed, timedOut]);
    // Note: This could be part of `tick` but is separate to keep the code
    // and dependencies cleaner. This also has the nice benefit of calling
    // `onTick` on the initial render.
    useEffect(() => {
        if (disabled)
            return;
        onTick({ secondsElapsed, timedOut });
    }, [onTick, disabled, secondsElapsed, timedOut]);
    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, []);
    const secondsRemaining = timeout - secondsElapsed;
    const countdownText = formatSeconds(secondsRemaining);
    const handleTypedTextChange = (event) => {
        const typedText = event.target.value;
        setTypedText(typedText);
        if (onChange) {
            onChange(typedText);
        }
    };
    const useFreeText = hasImage || allowFreeText;
    return (jsxs(Fragment, { children: [hasImage && (jsx("div", { children: jsx("img", { src: imageSrc, alt: imageAltText }, void 0) }, void 0)),
            jsx("div", Object.assign({ className: styles.elapsed }, { children: countdownText }), void 0),
            jsxs("div", Object.assign({ className: styles.container }, { children: [jsx("div", Object.assign({ className: styles.text, style: {
                            ...backgroundTextStyle,
                            color: useFreeText ? 'transparent' : backgroundTextColor,
                        } }, { children: jsx(TextDiff, { originalText: useFreeText
                                ? // If we have an image, the actual text here doesn't
                                    // matter since it won't be visible; we just need it to
                                    // take up the same amount of space as what's been typed.
                                    typedText || '.'
                                : text, compareText: typedText, showCaret: !useNativeCaret && !textBoxDisabled, typedTextStyle: { color: overlayTextColor }, caretStyle: {
                                backgroundColor: overlayTextColor,
                                opacity: focused ? 1 : 0.5,
                            } }, void 0) }), void 0),
                    jsx("textarea", { value: typedText, onChange: handleTypedTextChange, onKeyDown: (event) => onKeyPress(event.key), onFocus: () => setFocused(true), onBlur: () => setFocused(false), onPaste: (event) => event.preventDefault(), maxLength: useFreeText ? undefined : text.length, spellCheck: "false", disabled: textBoxDisabled, className: styles.input, style: {
                            caretColor: useNativeCaret ? overlayTextColor : undefined,
                            ...textareaStyle,
                        }, ref: inputRef }, void 0)] }), void 0)] }, void 0));
};

export { TypingTest, formatSeconds };
